/**
 * Button, IconButton and Spinner.
 *
 * A button initiates an action. Tabs navigate, segmented controls choose a mode,
 * toggles hold state — none of those live here, and none of them should look
 * like this. `variant` encodes emphasis rather than colour, so "there is one
 * primary action in this cluster" is expressible in the type system.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'destructive';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant;
  size?: 'md' | 'lg';
  /** Keeps the button's width and blocks a second submission. */
  loading?: boolean;
  icon?: IconName;
  iconPosition?: 'start' | 'end';
  block?: boolean;
  children: ReactNode;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon,
  iconPosition = 'start',
  block = false,
  disabled,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = [
    'mm-button',
    `mm-button--${variant}`,
    size === 'lg' ? 'mm-button--lg' : '',
    block ? 'mm-button--block' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={classes}
      // A loading button is not merely styled as busy — it cannot be pressed
      // again, which is what actually prevents a double signup.
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      <span
        className={loading ? 'mm-button__label--loading' : undefined}
        // Hidden from assistive technology while loading, because it is only
        // holding the width open at that point; the readable copy is below.
        aria-hidden={loading || undefined}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}
      >
        {icon && iconPosition === 'start' ? <Icon name={icon} size={18} /> : null}
        {children}
        {icon && iconPosition === 'end' ? <Icon name={icon} size={18} /> : null}
      </span>
      {loading ? (
        <>
          {/* Keeps the button's accessible name while it is busy. Without this
              a screen reader announces an unnamed button, which axe flags as a
              critical failure and a person experiences as a dead control. */}
          <span className="mm-visually-hidden">{children}</span>
          <span className="mm-button__spinner">
            <Spinner size={18} />
          </span>
        </>
      ) : null}
    </button>
  );
}

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'aria-label'> {
  icon: IconName;
  /** Required: an icon-only control with no accessible name is unusable. */
  label: string;
  /** Present makes this a toggle rather than an action. */
  pressed?: boolean;
  size?: number;
}

export function IconButton({
  icon,
  label,
  pressed,
  size = 20,
  type = 'button',
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      className="mm-icon-button"
      aria-label={label}
      // Tooltip for a pointer, accessible name for everything else.
      title={label}
      aria-pressed={pressed}
      {...rest}
    >
      <Icon name={icon} size={size} />
    </button>
  );
}

export function Spinner({ size = 20, label }: { size?: number; label?: string }) {
  return (
    <>
      <span
        className="mm-spinner"
        style={{ width: size, height: size }}
        aria-hidden="true"
      />
      {label ? <span className="mm-visually-hidden">{label}</span> : null}
    </>
  );
}
